import React, { useRef, useState, Suspense } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";

// 3D 카드 컴포넌트
function Card3D({
  position,
  rotation,
  imageUrl,
  onClick,
  isActive,
  cardIndex,
}) {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  // 실제 이미지 텍스처 로드
  const texture = useLoader(
    THREE.TextureLoader,
    imageUrl || "/images/ai_prod_01.jpg"
  );

  useFrame((state, delta) => {
    if (meshRef.current) {
      // 호버 효과
      const targetScale = hovered || isActive ? 1.1 : 1;
      meshRef.current.scale.lerp(
        { x: targetScale, y: targetScale, z: targetScale },
        0.1
      );

      // 물속 유영 효과 - 부유하는 Y축 움직임
      const time = state.clock.elapsedTime;
      const floatOffset = cardIndex * 0.5; // 각 카드마다 다른 위상
      meshRef.current.position.y = Math.sin(time * 0.8 + floatOffset) * 0.3;

      // 부드러운 기울기 효과 (물결처럼)
      meshRef.current.rotation.x = Math.sin(time * 0.6 + floatOffset) * 0.1;
      meshRef.current.rotation.z = Math.cos(time * 0.4 + floatOffset) * 0.05;

      // 부드러운 회전 효과
      if (isActive) {
        meshRef.current.rotation.y += delta * 0.3; // 속도 감소
      }
    }
  });

  return (
    <mesh
      ref={meshRef}
      position={position}
      rotation={rotation}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <planeGeometry args={[2, 3]} />
      <meshStandardMaterial map={texture} side={THREE.DoubleSide} />
      {/* 카드 프레임 */}
      <lineSegments>
        <edgesGeometry args={[new THREE.PlaneGeometry(2, 3)]} />
        <lineBasicMaterial color="#ffffff" linewidth={0} />
      </lineSegments>
    </mesh>
  );
}

// RGB 컬러 파티클 효과 컴포넌트
function RGBParticles() {
  const rgbGroupRef = useRef();
  const particleCount = 50;
  const particleRadius = 10;

  // RGB 컬러 배열
  const rgbColors = [
    "#FF0000", // Red
    "#00FF00", // Green
    "#0000FF", // Blue
    "#FF00FF", // Magenta
    "#FFFF00", // Yellow
    "#00FFFF", // Cyan
  ];

  // RGB 파티클 회전 애니메이션
  useFrame((state, delta) => {
    if (rgbGroupRef.current) {
      rgbGroupRef.current.rotation.y -= delta * 0.15;
      rgbGroupRef.current.rotation.x += delta * 0.08;
      rgbGroupRef.current.rotation.z += delta * 0.12;

      // 개별 파티클 펄스 효과
      rgbGroupRef.current.children.forEach((particle, index) => {
        if (particle.material) {
          const time = state.clock.elapsedTime;
          const pulse = Math.sin(time * 2 + index * 0.5) * 0.3 + 0.7;
          particle.material.opacity = pulse * 0.8;
          particle.scale.setScalar(pulse * 1.2);
        }
      });
    }
  });

  // RGB 파티클들을 3차원 공간에 배치
  const rgbParticles = Array.from({ length: particleCount }, (_, index) => {
    const phi = Math.acos(-1 + (2 * index) / particleCount);
    const theta = Math.sqrt(particleCount * Math.PI) * phi;

    const x = particleRadius * Math.cos(theta) * Math.sin(phi);
    const y = particleRadius * Math.sin(theta) * Math.sin(phi);
    const z = particleRadius * Math.cos(phi);

    const size = Math.random() * 0.15 + 0.05;
    const color = rgbColors[index % rgbColors.length];

    return (
      <mesh key={`rgb-${index}`} position={[x, y, z]}>
        <sphereGeometry args={[size, 6, 6]} />
        <meshBasicMaterial color={color} transparent opacity={0.7} />
      </mesh>
    );
  });

  return <group ref={rgbGroupRef}>{rgbParticles}</group>;
}

// CMYK 컬러 파티클 효과 컴포넌트
function CMYKParticles() {
  const cmykGroupRef = useRef();
  const particleCount = 40;
  const particleRadius = 7;

  // CMYK 컬러 배열
  const cmykColors = [
    "#00FFFF", // Cyan
    "#FF00FF", // Magenta
    "#FFFF00", // Yellow
    "#000000", // Key (Black)
    "#FF8080", // Light Red
    "#80FF80", // Light Green
    "#8080FF", // Light Blue
    "#808080", // Gray
  ];

  // CMYK 파티클 회전 애니메이션
  useFrame((state, delta) => {
    if (cmykGroupRef.current) {
      cmykGroupRef.current.rotation.y += delta * 0.1;
      cmykGroupRef.current.rotation.x -= delta * 0.06;
      cmykGroupRef.current.rotation.z += delta * 0.08;

      // 개별 파티클 플로팅 효과
      cmykGroupRef.current.children.forEach((particle, index) => {
        if (particle.material) {
          const time = state.clock.elapsedTime;
          const float = Math.cos(time * 1.5 + index * 0.8) * 0.4 + 0.6;
          particle.material.opacity = float * 0.6;

          // 위치 변화로 떠다니는 효과
          const originalY = particle.userData.originalY || particle.position.y;
          particle.userData.originalY = originalY;
          particle.position.y = originalY + Math.sin(time + index) * 0.5;
        }
      });
    }
  });

  // CMYK 파티클들을 3차원 공간에 배치
  const cmykParticles = Array.from({ length: particleCount }, (_, index) => {
    const angle = (index / particleCount) * Math.PI * 2;
    const radius = particleRadius + Math.sin(index * 0.5) * 2;
    const height = (Math.random() - 0.5) * 6;

    const x = Math.cos(angle) * radius;
    const y = height;
    const z = Math.sin(angle) * radius;

    const size = Math.random() * 0.12 + 0.04;
    const color = cmykColors[index % cmykColors.length];

    return (
      <mesh key={`cmyk-${index}`} position={[x, y, z]}>
        <octahedronGeometry args={[size, 0]} />
        <meshBasicMaterial color={color} transparent opacity={0.5} />
      </mesh>
    );
  });

  return <group ref={cmykGroupRef}>{cmykParticles}</group>;
}

// 배경 사선 효과 컴포넌트
function BackgroundLines() {
  // 5개의 사선을 다양한 각도로 배치
  const lines = [
    { angle: 30, position: [-8, 0, -5], length: 20 },
    { angle: 45, position: [8, 0, -6], length: 18 },
    { angle: -30, position: [0, 3, -7], length: 22 },
    { angle: 45, position: [-6, -2, -5], length: 16 },
    { angle: -45, position: [6, 2, -8], length: 19 },
  ];

  const backgroundLines = lines.map((line, index) => {
    const points = [];
    const startX =
      line.position[0] -
      (line.length / 2) * Math.cos((line.angle * Math.PI) / 180);
    const startY =
      line.position[1] -
      (line.length / 2) * Math.sin((line.angle * Math.PI) / 180);
    const endX =
      line.position[0] +
      (line.length / 2) * Math.cos((line.angle * Math.PI) / 180);
    const endY =
      line.position[1] +
      (line.length / 2) * Math.sin((line.angle * Math.PI) / 180);

    points.push(new THREE.Vector3(startX, startY, line.position[2]));
    points.push(new THREE.Vector3(endX, endY, line.position[2]));

    const geometry = new THREE.BufferGeometry().setFromPoints(points);

    return (
      <line key={`bg-line-${index}`}>
        <primitive object={geometry} />
        <lineBasicMaterial
          color="#D3D3D3" // Light Gray
          transparent
          opacity={0.4}
          linewidth={2}
        />
      </line>
    );
  });

  return <group>{backgroundLines}</group>;
}

// 점선 궤도 효과 컴포넌트
function OrbitLines() {
  const orbitGroupRef = useRef();

  useFrame((state, delta) => {
    if (orbitGroupRef.current) {
      // 카드와 다른 각도로 회전
      orbitGroupRef.current.rotation.y += delta * 0.1;
      orbitGroupRef.current.rotation.x += delta * 0.08;
    }
  });

  // 여러 개의 점선 궤도 생성
  const orbits = Array.from({ length: 3 }, (_, index) => {
    const radius = 6 + index * 1.5;
    const points = [];
    const segments = 60;

    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const z = Math.sin(angle) * radius;
      const y = Math.sin(angle * 3) * 0.5; // 물결 효과
      points.push(new THREE.Vector3(x, y, z));
    }

    const geometry = new THREE.BufferGeometry().setFromPoints(points);

    return (
      <line key={index}>
        <primitive object={geometry} />
        <lineBasicMaterial
          color="#4ecdc4"
          transparent
          opacity={0.3}
          linewidth={1}
        />
      </line>
    );
  });

  return <group ref={orbitGroupRef}>{orbits}</group>;
}

// 3D 캐로셀 컴포넌트
function Carousel3D() {
  const groupRef = useRef();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);

  const cardCount = 12;
  const radius = 5;

  // 자동 회전 애니메이션 (더 부드럽게)
  useFrame((state, delta) => {
    if (groupRef.current && autoRotate) {
      // 부드러운 회전 속도
      groupRef.current.rotation.y += delta * 0.15;

      // 전체 캐로셀에 살짝 흔들리는 효과 (바다 조류처럼)
      const time = state.clock.elapsedTime;
      groupRef.current.position.y = 1 + Math.sin(time * 0.3) * 0.1; // 기본 위치를 1 단위 위로 (50px 하향 조정)
      groupRef.current.rotation.x = Math.sin(time * 0.2) * 0.02;
    }
  });

  // 12장의 이미지 배열
  const imageUrls = [
    "/images/ai_prod_01.jpg",
    "/images/ai_prod_02.jpg",
    "/images/ai_prod_03.jpg",
    "/images/ai_prod_04.jpg",
    "/images/ai_prod_05.jpg",
    "/images/ai_prod_06.jpg",
    "/images/ai_prod_07.jpg",
    "/images/ai_prod_08.jpg",
    "/images/ai_prod_09.jpg",
    "/images/ai_prod_10.jpg",
    "/images/ai_prod_11.jpg",
    "/images/ai_prod_12.jpg",
  ];

  // 카드들을 원형으로 배치 (중심을 향하도록)
  const cards = Array.from({ length: cardCount }, (_, index) => {
    const angle = (index / cardCount) * Math.PI * 2;
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    // 카드가 중심점을 바라보도록 각도 조정
    const rotationY = angle;

    return (
      <Card3D
        key={index}
        cardIndex={index}
        position={[x, 0, z]}
        rotation={[0, rotationY, 0]}
        imageUrl={imageUrls[index]}
        isActive={index === currentIndex}
        onClick={() => {
          setCurrentIndex(index);
          setAutoRotate(!autoRotate);
        }}
      />
    );
  });

  return <group ref={groupRef}>{cards}</group>;
}

export default function Main() {
  return (
    <main
      className="app-main"
      style={{
        height: "100vh",
        width: "100vw",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Canvas
        camera={{ position: [0, 2, 10], fov: 60 }}
        style={{ background: "white" }}
      >
        {/* 조명 설정 */}
        <ambientLight intensity={0.6} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <pointLight position={[-10, 10, -5]} intensity={0.5} />

        {/* 배경 사선 효과 */}
        <BackgroundLines />

        {/* RGB 파티클 효과 */}
        <RGBParticles />

        {/* CMYK 파티클 효과 */}
        <CMYKParticles />

        {/* 점선 궤도 효과 */}
        <OrbitLines />

        {/* 3D 캐로셀 */}
        <Suspense fallback={null}>
          <Carousel3D />
        </Suspense>

        {/* 카메라 컨트롤 */}
        <OrbitControls
          enablePan={false}
          enableZoom={true}
          enableRotate={true}
          minDistance={5}
          maxDistance={20}
          maxPolarAngle={Math.PI / 2}
        />
      </Canvas>
    </main>
  );
}
